import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import salesService from '../../../../services/salesService';
import {
  clubEntryPackageService,
  ClubEntryPackageDto,
  CreateClubEntryPackageItemDto,
} from '../../../../services/clubEntryPackageService';

interface ClubEntryPackagesManagerProps {
  screenId: number;
  companyId: string;
  onChanged?: () => void;
}

interface FormItem {
  productId: string | null;
  productName: string;
  quantity: number;
  unitPrice: number; // sadece dialog gösterimi için, backend'e gönderilmez
}

interface ProductOption {
  id: string;
  name: string;
  salePrice: number;
}

const PRESET_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0097a7', '#5d4037', '#455a64'];

const ClubEntryPackagesManager: React.FC<ClubEntryPackagesManagerProps> = ({ screenId, onChanged }) => {
  const [packages, setPackages] = useState<ClubEntryPackageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(PRESET_COLORS[0]);
  const [items, setItems] = useState<FormItem[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchOptions, setSearchOptions] = useState<ProductOption[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Silme onayı: native window.confirm POS/Electron ortamında bastırılabildiği
  // için uygulama içi dialog kullanıyoruz.
  const [deleteTarget, setDeleteTarget] = useState<ClubEntryPackageDto | null>(null);

  const loadPackages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clubEntryPackageService.getPackages(screenId);
      setPackages(data);
    } catch (err) {
      console.error('[ClubEntryPackages] Yüklenemedi:', err);
      setError('Paketler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [screenId]);

  useEffect(() => { loadPackages(); }, [loadPackages]);

  const runSearch = useCallback((term: string) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!term || term.trim().length < 2) {
      setSearchOptions([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const results = await salesService.searchProducts(term.trim());
        const seen = new Set<string>();
        const opts: ProductOption[] = [];
        for (const p of results) {
          const id = String(p.id || p.Id || '');
          if (!id || seen.has(id)) continue;
          seen.add(id);
          opts.push({
            id,
            name: p.name || p.Name || '',
            salePrice: Number(p.salePrice ?? p.SalePrice ?? 0),
          });
        }
        setSearchOptions(opts);
      } catch (err) {
        console.error('[ClubEntryPackages] Ürün arama hatası:', err);
        setSearchOptions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleOpenCreate = () => {
    setEditingId(null);
    setName('');
    setColor(PRESET_COLORS[0]);
    setItems([]);
    setSearchTerm('');
    setSearchOptions([]);
    setDialogOpen(true);
  };

  const handleOpenEdit = (pkg: ClubEntryPackageDto) => {
    setEditingId(pkg.id);
    setName(pkg.name);
    setColor(pkg.color || PRESET_COLORS[0]);
    setItems(pkg.items.map(it => ({
      productId: it.productId ?? null,
      productName: it.productName,
      quantity: it.quantity,
      unitPrice: 0,
    })));
    setSearchTerm('');
    setSearchOptions([]);
    setDialogOpen(true);
  };

  const handleAddProduct = (opt: ProductOption | null) => {
    if (!opt) return;
    setItems(prev => {
      const existingIdx = prev.findIndex(i => i.productId === opt.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], quantity: next[existingIdx].quantity + 1 };
        return next;
      }
      return [...prev, { productId: opt.id, productName: opt.name, quantity: 1, unitPrice: opt.salePrice }];
    });
    setSearchTerm('');
    setSearchOptions([]);
  };

  const handleItemQuantityChange = (index: number, value: string) => {
    const qty = parseFloat(value);
    setItems(prev => prev.map((it, i) => i === index ? { ...it, quantity: isNaN(qty) || qty <= 0 ? 1 : qty } : it));
  };

  const handleRemoveItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Paket adı gerekli.'); return; }
    if (items.length === 0) { setError('Pakete en az bir ürün ekleyin.'); return; }
    try {
      setSaving(true);
      setError(null);
      const payloadItems: CreateClubEntryPackageItemDto[] = items.map((it, idx) => ({
        productId: it.productId,
        productName: it.productName,
        quantity: it.quantity,
        sortOrder: idx,
      }));
      if (editingId) {
        await clubEntryPackageService.updatePackage(editingId, {
          name: name.trim(),
          color,
          sortOrder: 0,
          items: payloadItems,
        });
      } else {
        await clubEntryPackageService.createPackage({
          quickSaleScreenId: screenId,
          name: name.trim(),
          color,
          sortOrder: packages.length,
          items: payloadItems,
        });
      }
      setDialogOpen(false);
      await loadPackages();
      onChanged?.();
    } catch (err) {
      console.error('[ClubEntryPackages] Kaydedilemedi:', err);
      setError('Paket kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      setError(null);
      await clubEntryPackageService.deletePackage(deleteTarget.id);
      setDeleteTarget(null);
      await loadPackages();
      onChanged?.();
    } catch (err) {
      console.error('[ClubEntryPackages] Silinemedi:', err);
      setError('Paket silinemedi.');
      setDeleteTarget(null);
    } finally {
      setSaving(false);
    }
  };

  const dialogTotal = items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2Icon color="primary" />
          <Box>
            <Typography variant="h6">Paket Ürünler</Typography>
            <Typography variant="body2" color="text.secondary">
              Birden fazla ürünü tek bir başlık altında toplayın. Hızlı satış ekranında tek kart gibi görünür; tıklanınca içindeki ürünler sepete eklenir.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Yeni Paket
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      {packages.length === 0 ? (
        <Alert severity="info">Henüz paket oluşturulmadı. "Yeni Paket" ile başlayın.</Alert>
      ) : (
        <List>
          {packages.map(pkg => (
            <Paper key={pkg.id} variant="outlined" sx={{ mb: 1 }}>
              <ListItem
                secondaryAction={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Düzenle">
                      <IconButton edge="end" onClick={() => handleOpenEdit(pkg)} disabled={saving}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                      <IconButton edge="end" color="error" onClick={() => setDeleteTarget(pkg)} disabled={saving}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
              >
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: pkg.color || '#ccc', mr: 2, flexShrink: 0 }} />
                <ListItemText
                  primary={pkg.name}
                  secondary={`${pkg.items.length} ürün`}
                />
              </ListItem>
            </Paper>
          ))}
        </List>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Paketi Düzenle' : 'Yeni Paket'}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Paket Adı"
            placeholder="örn. Yetişkin"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            autoFocus
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Renk</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {PRESET_COLORS.map(c => (
              <Box
                key={c}
                onClick={() => setColor(c)}
                sx={{
                  width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                  border: color === c ? '3px solid' : '2px solid transparent',
                  borderColor: color === c ? 'text.primary' : 'transparent',
                }}
              />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Ürün Ekle</Typography>
          <Autocomplete
            options={searchOptions}
            getOptionLabel={(o) => o.name}
            filterOptions={(x) => x}
            loading={searchLoading}
            inputValue={searchTerm}
            onInputChange={(_, value) => { setSearchTerm(value); runSearch(value); }}
            onChange={(_, value) => handleAddProduct(value)}
            value={null}
            blurOnSelect
            clearOnBlur
            noOptionsText={searchTerm.length < 2 ? 'En az 2 karakter yazın' : 'Ürün bulunamadı'}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span>{option.name}</span>
                  <span style={{ opacity: 0.6 }}>{option.salePrice.toFixed(2)} ₺</span>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ürün adı veya barkod ara..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {items.length > 0 && (
            <List sx={{ mt: 1 }}>
              {items.map((it, idx) => (
                <ListItem
                  key={`${it.productId}-${idx}`}
                  secondaryAction={
                    <IconButton edge="end" color="error" onClick={() => handleRemoveItem(idx)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                  sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  <ListItemText
                    primary={it.productName}
                    secondary={it.unitPrice > 0 ? `${it.unitPrice.toFixed(2)} ₺` : undefined}
                  />
                  <TextField
                    type="number"
                    size="small"
                    label="Adet"
                    value={it.quantity}
                    onChange={(e) => handleItemQuantityChange(idx, e.target.value)}
                    sx={{ width: 90, mr: 5 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {items.length > 0 && dialogTotal > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Chip label={`Toplam: ${dialogTotal.toFixed(2)} ₺`} color="primary" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>İptal</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => !saving && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Paketi Sil</DialogTitle>
        <DialogContent dividers>
          <Typography>
            <strong>{deleteTarget?.name}</strong> paketini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>İptal</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClubEntryPackagesManager;
